import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader, Message, Tooltip, Whisper } from 'rsuite';

import { useProjects } from '../hooks/useQueries';
import useStaffingOutlookModel, { monthKind } from '../hooks/useStaffingOutlookModel';
import { exportSheetsToXlsx } from '../utils/excelExport';
import './TaskOrderDetailPage.css';
import './StaffingOutlookPage.css';

/*
 * Staffing outlook: every person on the task order by month, with the months that are
 * over counted at their actual hours and the months still to come at their forecast.
 * Priced at each person's rate for that month, plus ODCs, that gives where the task order
 * is headed against its budget and how many hours a month would close the gap.
 *
 * The current month is in progress, so it counts whichever is higher: what has been
 * logged so far or what was forecast. Actuals only ever grow within a month, so the
 * forecast is the floor until someone logs past it.
 */

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const currencyCompact = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 2,
});

const formatMoney = (value) => (Number.isFinite(Number(value)) ? currency.format(Number(value)) : '—');
const formatMoneyCompact = (value) => (Number.isFinite(Number(value)) ? currencyCompact.format(Number(value)) : '—');

const formatHours = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return '';
  return n.toLocaleString(undefined, { maximumFractionDigits: Math.abs(n) >= 100 ? 0 : 1 });
};

const formatHoursAlways = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
};

const formatPct = (ratio, digits = 1) => {
  const n = Number(ratio);
  if (!Number.isFinite(n)) return '—';
  return `${(n * 100).toFixed(digits)}%`;
};

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatMonthShort = (ms) => {
  const [y, m] = String(ms || '').split('-').map(Number);
  if (!Number.isInteger(y) || !Number.isInteger(m)) return '—';
  return `${MONTHS_SHORT[m - 1]} '${String(y).slice(-2)}`;
};
const formatMonthLong = (ms) => {
  const [y, m] = String(ms || '').split('-').map(Number);
  if (!Number.isInteger(y) || !Number.isInteger(m)) return '—';
  return new Date(y, m - 1, 1).toLocaleString(undefined, { month: 'long', year: 'numeric' });
};

const niceStep = (range, targetTicks = 4) => {
  if (!(range > 0)) return 1;
  const raw = range / targetTicks;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const unit = raw / pow;
  const nice = unit <= 1 ? 1 : unit <= 2 ? 2 : unit <= 2.5 ? 2.5 : unit <= 5 ? 5 : 10;
  return nice * pow;
};

// Cumulative spend against budget. Solid where the months are over (actuals), dashed where
// they are still forecast, so the eye reads "where we are" and "where we are headed" as one
// line with a change of certainty at today.
function BurnChart({ months, series, budget, currentMonth, endMonth }) {
  const [el, setEl] = useState(null);
  const [width, setWidth] = useState(0);
  const [hoverIdx, setHoverIdx] = useState(null);

  useEffect(() => {
    if (!el) return undefined;
    setWidth(el.clientWidth);
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);

  const height = 260;
  const pad = { top: 22, right: 20, bottom: 30, left: 64 };
  const plotW = Math.max(10, width - pad.left - pad.right);
  const plotH = height - pad.top - pad.bottom;

  const maxCum = series.length ? series[series.length - 1].cumulative : 0;
  const yTop = Math.max(Number(budget) || 0, maxCum) * 1.1 || 1;
  const step = niceStep(yTop);
  const yMax = Math.ceil(yTop / step) * step;
  const ticks = [];
  for (let v = 0; v <= yMax + step / 2; v += step) ticks.push(v);

  const band = plotW / Math.max(1, months.length);
  const x = (i) => pad.left + band * i + band / 2;
  const y = (v) => pad.top + plotH - (v / yMax) * plotH;

  const lastActualIdx = series.reduce((acc, p, i) => (p.kind === 'actual' ? i : acc), -1);
  const toPath = (pts) => pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');

  const actualPts = [[pad.left, y(0)], ...series.slice(0, lastActualIdx + 1).map((p, i) => [x(i), y(p.cumulative)])];
  const projectedStart = lastActualIdx >= 0 ? [x(lastActualIdx), y(series[lastActualIdx].cumulative)] : [pad.left, y(0)];
  const projectedPts = [projectedStart, ...series.slice(lastActualIdx + 1).map((p, i) => [x(lastActualIdx + 1 + i), y(p.cumulative)])];
  const actualArea = actualPts.length > 1
    ? `${toPath(actualPts)} L ${actualPts[actualPts.length - 1][0].toFixed(1)} ${y(0)} Z`
    : '';

  const currentIdx = months.indexOf(currentMonth);
  const endIdx = endMonth ? months.indexOf(endMonth) : -1;
  const labelEvery = Math.max(1, Math.ceil(months.length / Math.max(1, Math.floor(plotW / 56))));
  const hovered = hoverIdx !== null ? series[hoverIdx] : null;
  const last = series[series.length - 1];

  return (
    <div className="ppc-so-chart" ref={setEl}>
      {width > 0 ? (
        <svg width={width} height={height} role="img" aria-label="Cumulative spend against budget by month">
          {ticks.map((t) => (
            <g key={t}>
              <line className="ppc-so-chart__grid" x1={pad.left} x2={width - pad.right} y1={y(t)} y2={y(t)} />
              <text className="ppc-so-chart__axis" x={pad.left - 8} y={y(t)} textAnchor="end" dominantBaseline="middle">
                {formatMoneyCompact(t)}
              </text>
            </g>
          ))}

          {months.map((ms, i) => (i % labelEvery === 0 ? (
            <text key={ms} className="ppc-so-chart__axis" x={x(i)} y={height - 10} textAnchor="middle">{formatMonthShort(ms)}</text>
          ) : null))}

          {currentIdx >= 0 ? (
            <g>
              <line className="ppc-so-chart__marker" x1={x(currentIdx)} x2={x(currentIdx)} y1={pad.top} y2={pad.top + plotH} />
              <text className="ppc-so-chart__marker-label" x={x(currentIdx) + 4} y={pad.top + 10}>Today</text>
            </g>
          ) : null}
          {endIdx >= 0 && endIdx < months.length - 1 ? (
            <g>
              <line className="ppc-so-chart__marker ppc-so-chart__marker--end" x1={x(endIdx) + band / 2} x2={x(endIdx) + band / 2} y1={pad.top} y2={pad.top + plotH} />
              <text className="ppc-so-chart__marker-label" x={x(endIdx) + band / 2 + 4} y={pad.top + 24}>Task order ends</text>
            </g>
          ) : null}

          {budget > 0 ? (
            <g>
              <line className="ppc-so-chart__budget" x1={pad.left} x2={width - pad.right} y1={y(budget)} y2={y(budget)} />
              <text className="ppc-so-chart__budget-label" x={pad.left + 6} y={y(budget) - 6}>Budget {formatMoneyCompact(budget)}</text>
            </g>
          ) : null}

          {actualArea ? <path className="ppc-so-chart__area" d={actualArea} /> : null}
          {actualPts.length > 1 ? <path className="ppc-so-chart__line" d={toPath(actualPts)} /> : null}
          {projectedPts.length > 1 ? <path className="ppc-so-chart__line ppc-so-chart__line--projected" d={toPath(projectedPts)} /> : null}

          {last ? (
            <g>
              <circle className="ppc-so-chart__end" cx={x(series.length - 1)} cy={y(last.cumulative)} r={4} />
              <text className="ppc-so-chart__end-label" x={x(series.length - 1) - 8} y={y(last.cumulative) - 10} textAnchor="end">
                Projected {formatMoneyCompact(last.cumulative)}
              </text>
            </g>
          ) : null}

          {hovered ? (
            <g>
              <line className="ppc-so-chart__crosshair" x1={x(hoverIdx)} x2={x(hoverIdx)} y1={pad.top} y2={pad.top + plotH} />
              <circle className="ppc-so-chart__dot" cx={x(hoverIdx)} cy={y(hovered.cumulative)} r={4.5} />
            </g>
          ) : null}

          {months.map((ms, i) => (
            <rect
              key={ms}
              x={pad.left + band * i}
              y={pad.top}
              width={band}
              height={plotH}
              fill="transparent"
              onMouseEnter={() => setHoverIdx(i)}
              onMouseLeave={() => setHoverIdx(null)}
            />
          ))}
        </svg>
      ) : null}

      {hovered ? (
        <div
          className="ppc-so-chart__tip"
          style={{
            left: Math.min(Math.max(x(hoverIdx), 110), Math.max(110, width - 110)),
            top: Math.max(0, y(hovered.cumulative) - 12),
          }}
        >
          <div className="ppc-so-chart__tip-title">
            {formatMonthLong(hovered.monthStart)}
            <span className={`ppc-so-kind ppc-so-kind--${hovered.kind}`}>
              {hovered.kind === 'actual' ? 'Actual' : hovered.kind === 'current' ? 'In progress' : 'Forecast'}
            </span>
          </div>
          <dl>
            <dt>This month</dt><dd>{formatMoney(hovered.monthly)}</dd>
            <dt>Cumulative</dt><dd>{formatMoney(hovered.cumulative)}</dd>
            {budget > 0 ? (<><dt>Of budget</dt><dd>{formatPct(hovered.cumulative / budget)}</dd></>) : null}
          </dl>
        </div>
      ) : null}
    </div>
  );
}

// Column header that sorts its column. aria-sort sits on the th; the arrow only shows on
// the active column so an unsorted table stays quiet.
function SortableTh({ sortKey, sort, onSort, className = '', align = 'right', children }) {
  const active = sort.key === sortKey;
  return (
    <th
      scope="col"
      className={`${className} ppc-so-th`.trim()}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button type="button" className={`ppc-so-sort ppc-so-sort--${align}${active ? ' is-active' : ''}`} onClick={() => onSort(sortKey)}>
        <span>{children}</span>
        <i className={`bi ${active && sort.dir === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'}`} aria-hidden="true" />
      </button>
    </th>
  );
}

export default function StaffingOutlookPage() {
  const navigate = useNavigate();
  const params = useParams();
  const projectId = Number(params?.projectId);
  const taskOrderId = Number(params?.taskOrderId);

  const [unit, setUnit] = useState('hours');
  const [exporting, setExporting] = useState(false);
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });

  const { data: projects } = useProjects();
  const project = useMemo(
    () => (Array.isArray(projects) ? projects : projects?.projects || []).find((p) => Number(p?.id) === projectId) || null,
    [projectId, projects]
  );

  const { data, isLoading, error, model, currentMonth, months, startMonth, endMonth, budget } = useStaffingOutlookModel(
    projectId,
    taskOrderId
  );

  const hasOdc = model.odcTotal !== 0 || Object.values(data?.odcByMonth || {}).some((e) => Number(e?.forecast) || Number(e?.actual));
  const isDollars = unit === 'dollars';

  // The table only covers the task order's own months. Hours outside it still count in
  // the tiles and chart above (the warning there says so), but they are not work on
  // this task order's period, so they are left out here.
  const tableMonths = useMemo(
    () => months.filter((ms) => (!startMonth || ms >= startMonth) && (!endMonth || ms <= endMonth)),
    [endMonth, months, startMonth]
  );
  const hiddenMonthCount = months.length - tableMonths.length;

  const table = useMemo(() => {
    const rows = model.people
      .map((p) => {
        let hours = 0;
        let dollars = 0;
        // Rates in effect over the months shown, in date order, one entry per run of
        // months at the same rate: [{ rate, from, to }].
        const rates = [];
        for (const ms of tableMonths) {
          const c = p.cells[ms];
          hours += c.hours;
          dollars += c.dollars;
          const stored = p.months?.[ms];
          const rate = Number(stored?.rate ?? 0);
          if (!stored || !(c.hours > 0 || c.forecastHours > 0 || c.actualHours > 0)) continue;
          const last = rates[rates.length - 1];
          if (last && Math.abs(last.rate - rate) < 0.005) last.to = ms;
          else rates.push({ rate, from: ms, to: ms });
        }
        return { ...p, tHours: hours, tDollars: dollars, rates };
      })
      .filter((p) => p.tHours > 0);

    const odcCells = model.odcCells;
    const odcTotal = tableMonths.reduce((acc, ms) => acc + odcCells[ms].dollars, 0);
    const monthTotals = {};
    let cumulative = 0;
    for (const ms of tableMonths) {
      const hours = rows.reduce((acc, p) => acc + p.cells[ms].hours, 0);
      const dollars = rows.reduce((acc, p) => acc + p.cells[ms].dollars, 0) + odcCells[ms].dollars;
      cumulative += dollars;
      monthTotals[ms] = { hours, dollars, cumulative };
    }
    const hours = rows.reduce((acc, p) => acc + p.tHours, 0);
    const dollars = rows.reduce((acc, p) => acc + p.tDollars, 0) + odcTotal;
    return { rows, odcTotal, monthTotals, hours, dollars };
  }, [model.odcCells, model.people, tableMonths]);

  const sortedRows = useMemo(() => {
    const field = isDollars ? 'dollars' : 'hours';
    const valueOf = (p) => {
      if (sort.key === 'name') return p.name.toLowerCase();
      if (sort.key === 'rate') return p.rates.length ? Math.max(...p.rates.map((r) => r.rate)) : 0;
      if (sort.key === 'total') return isDollars ? p.tDollars : p.tHours;
      // Share follows the Hours/Dollars toggle, and within one view it ranks the same as Total.
      if (sort.key === 'share') return isDollars ? p.tDollars : p.tHours;
      return p.cells[sort.key]?.[field] ?? 0;
    };
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...table.rows].sort((a, b) => {
      const va = valueOf(a);
      const vb = valueOf(b);
      const cmp = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
      return cmp * dir || a.name.localeCompare(b.name);
    });
  }, [isDollars, sort, table.rows]);

  // Names sort A-Z first; numbers biggest first, since that is usually the question.
  const toggleSort = useCallback((key) => {
    setSort((prev) => (prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: key === 'name' ? 'asc' : 'desc' }));
  }, []);

  const formatRateRun = (r) => (r.from === r.to ? formatMonthShort(r.from) : `${formatMonthShort(r.from)} \u2013 ${formatMonthShort(r.to)}`);
  const rateText = (p) => (p.rates.length
    ? p.rates
      .map((r) => {
        const label = r.rate > 0 ? formatMoney(r.rate) : 'No rate';
        return p.rates.length > 1 ? `${label} (${formatRateRun(r)})` : label;
      })
      .join('; ')
    : '');

  const handleExport = async () => {
    setExporting(true);
    try {
      const monthCols = tableMonths.map((ms) => ({ header: formatMonthShort(ms), key: ms, width: 11 }));
      const typeRow = { person: 'Counts', rate: '' };
      for (const ms of tableMonths) {
        const kind = monthKind(ms, currentMonth);
        typeRow[ms] = kind === 'actual' ? 'Actual' : kind === 'current' ? 'In progress' : 'Forecast';
      }
      const build = (field) => [
        typeRow,
        ...sortedRows.map((p) => {
          const row = { person: p.name, rate: rateText(p) };
          for (const ms of tableMonths) row[ms] = Number(p.cells[ms][field].toFixed(2));
          row.total = Number((field === 'dollars' ? p.tDollars : p.tHours).toFixed(2));
          return row;
        }),
      ];
      const dollarRows = build('dollars');
      if (hasOdc) {
        const row = { person: 'ODCs', rate: '' };
        for (const ms of tableMonths) row[ms] = Number(model.odcCells[ms].dollars.toFixed(2));
        row.total = Number(table.odcTotal.toFixed(2));
        dollarRows.push(row);
      }
      const baseCols = [
        { header: 'Person', key: 'person', width: 24 },
        { header: 'Rate', key: 'rate', width: 26 },
      ];
      await exportSheetsToXlsx({
        fileName: `staffing-outlook-${data?.taskOrderName || taskOrderId}.xlsx`,
        sheets: [
          { name: 'Hours', columns: [...baseCols, ...monthCols, { header: 'Total', key: 'total', width: 12 }], rows: build('hours') },
          {
            name: 'Dollars',
            columns: [
              ...baseCols,
              ...monthCols.map((c) => ({ ...c, numFmt: '$#,##0' })),
              { header: 'Total', key: 'total', width: 14, numFmt: '$#,##0' },
            ],
            rows: dollarRows,
          },
        ],
      });
    } finally {
      setExporting(false);
    }
  };

  const taskOrderPath = `/projects/${projectId}/task-orders/${taskOrderId}`;
  const cellText = (value) => (isDollars ? (value ? formatMoneyCompact(value) : '') : formatHours(value));

  return (
    <div className="ppc-page ppc-tod ppc-so">
      <header className="ppc-tod__header">
        <div style={{ minWidth: 0 }}>
          <div className="ppc-tod__crumbs">
            <button type="button" className="ppc-tod__crumb-link" onClick={() => navigate('/')}>Projects</button>
            <i className="bi bi-chevron-right ppc-tod__crumb-sep" aria-hidden="true" />
            <button type="button" className="ppc-tod__crumb-link" onClick={() => navigate(`/projects/${projectId}`)}>
              {project?.name || 'Project'}
            </button>
            <i className="bi bi-chevron-right ppc-tod__crumb-sep" aria-hidden="true" />
            <button type="button" className="ppc-tod__crumb-link" onClick={() => navigate(taskOrderPath)}>
              {data?.taskOrderName || 'Task order'}
            </button>
            <i className="bi bi-chevron-right ppc-tod__crumb-sep" aria-hidden="true" />
            <span className="ppc-tod__crumb-current">Staffing Outlook</span>
          </div>
          <h1 className="ppc-tod__title">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
              <i className="bi bi-people" aria-hidden="true" />
              Staffing Outlook
            </span>
          </h1>
        </div>

        <div className="ppc-tod__actions">
          <button type="button" className="ppc-tod-btn" onClick={() => navigate(taskOrderPath)}>
            <i className="bi bi-arrow-left" aria-hidden="true" />
            Back to task order
          </button>
          <div className="ppc-tod-segment">
            <button type="button" className="ppc-tod-btn" onClick={() => navigate(`${taskOrderPath}/staffing-outlook/scenarios`)}>
              Scenarios
              <i className="bi bi-sliders" aria-hidden="true" />
            </button>
            <button type="button" className="ppc-tod-btn" onClick={handleExport} disabled={!data || exporting || model.people.length === 0}>
              Export
              <i className="bi bi-file-earmark-excel" aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      {error ? (
        <Message type="error" showIcon>Could not load the staffing outlook: {String(error?.message || error)}</Message>
      ) : isLoading ? (
        <div className="ppc-so__loading"><Loader content="Loading outlook…" /></div>
      ) : (
        <>
          <section className="ppc-tod-metrics">
            <div className="ppc-tod-metric">
              <div className="ppc-tod-metric__body">
                <div>
                  <p className="ppc-tod-metric__label">Budget</p>
                  <p className="ppc-tod-metric__value ppc-tod-num">{budget !== null ? formatMoney(budget) : '—'}</p>
                </div>
                <div className="ppc-tod-metric__foot">
                  {budget === null ? 'No budget set' : data?.budgetSource === 'taskOrder' ? 'Task order budget' : 'Sum of discipline budgets'}
                </div>
              </div>
            </div>

            <div className="ppc-tod-metric">
              <div className="ppc-tod-metric__body">
                <div>
                  <p className="ppc-tod-metric__label">Projected total</p>
                  <p className="ppc-tod-metric__value ppc-tod-num">{formatMoney(model.projected)}</p>
                </div>
                <div className="ppc-tod-metric__foot ppc-tod-num">
                  {`${formatMoneyCompact(model.spentToDate)} spent + ${formatMoneyCompact(model.projected - model.spentToDate)} to come`}
                </div>
              </div>
            </div>

            <div className="ppc-tod-metric">
              <div className="ppc-tod-metric__body">
                <div>
                  <p className="ppc-tod-metric__label">{model.gap !== null && model.gap < 0 ? 'Over budget' : 'Left in budget'}</p>
                  <p className={`ppc-tod-metric__value ppc-tod-num${model.gap !== null && model.gap < 0 ? ' ppc-so-text--bad' : ''}`}>
                    {model.gap !== null ? formatMoney(Math.abs(model.gap)) : '—'}
                  </p>
                </div>
                <div className="ppc-tod-metric__foot ppc-tod-num">
                  {budget === null
                    ? 'No budget to compare'
                    : budget > 0
                      ? `Projected to use ${formatPct(model.projected / budget)} of budget`
                      : 'Budget is $0, so all spend is over'}
                </div>
              </div>
            </div>

            <div className="ppc-tod-metric">
              <div className="ppc-tod-metric__body">
                <div>
                  <p className="ppc-tod-metric__label">
                    {model.hoursPerMonth !== null && model.hoursPerMonth < 0 ? 'Hours to cut' : 'Hours to add'}
                  </p>
                  <p className="ppc-tod-metric__value ppc-tod-num">
                    {model.hoursPerMonth === null ? '—' : (
                      <>
                        {formatHoursAlways(Math.abs(model.hoursPerMonth))}
                        <span className="ppc-so-unit"> h/mo</span>
                      </>
                    )}
                  </p>
                </div>
                <div className="ppc-tod-metric__foot ppc-tod-num">
                  {model.hoursPerMonth === null
                    ? budget === null ? 'Needs a budget' : 'No months left in the task order'
                    : `Over ${model.remainingMonths.length} month${model.remainingMonths.length === 1 ? '' : 's'} at ${formatMoney(model.blendedRate)}/h blended`}
                </div>
              </div>
            </div>
          </section>

          {model.afterEnd.length > 0 || model.noRate.length > 0 ? (
            <div className="ppc-so__alerts">
              {model.afterEnd.length > 0 ? (
                <div className="ppc-tod-alert ppc-tod-alert--warning">
                  <i className="bi bi-calendar-x" aria-hidden="true" />
                  <span>
                    {formatHoursAlways(model.afterEnd.reduce((a, r) => a + r.hours, 0))} h
                    ({formatMoney(model.afterEnd.reduce((a, r) => a + r.dollars, 0))}) are forecast after the task order ends in{' '}
                    {formatMonthLong(endMonth)}: {model.afterEnd.map((r) => r.name).join(', ')}. They are included in the projected total.
                  </span>
                </div>
              ) : null}
              {model.noRate.length > 0 ? (
                <div className="ppc-tod-alert ppc-tod-alert--warning">
                  <i className="bi bi-currency-dollar" aria-hidden="true" />
                  <span>
                    {formatHoursAlways(model.noRate.reduce((a, r) => a + r.hours, 0))} h have no rate in effect for their month, so
                    they add $0 to the totals: {model.noRate.map((r) => `${r.name} (${formatHoursAlways(r.hours)} h)`).join(', ')}.
                    Set a rate with an earlier effective date to price them.
                  </span>
                </div>
              ) : null}
            </div>
          ) : null}

          <section className="ppc-tod-card">
            <div className="ppc-tod-card__head">
              <h2 className="ppc-tod-card__title">Spend against budget</h2>
              <div className="ppc-so-legend" aria-hidden="true">
                <span><span className="ppc-so-legend__line" /> Actual</span>
                <span><span className="ppc-so-legend__line ppc-so-legend__line--dashed" /> Projected</span>
                {budget > 0 ? <span><span className="ppc-so-legend__budget" /> Budget</span> : null}
              </div>
            </div>
            {model.series.length > 0 ? (
              <BurnChart months={months} series={model.series} budget={budget || 0} currentMonth={currentMonth} endMonth={endMonth} />
            ) : (
              <div className="ppc-tod-card__note">No hours or ODCs have been forecast or logged on this task order yet.</div>
            )}
          </section>

          <section className="ppc-tod-card">
            <div className="ppc-tod-card__head">
              <h2 className="ppc-tod-card__title">By person</h2>
              <div className="ppc-tod-card__tools">
                <div className="ppc-so-legend" aria-hidden="true">
                  <span><span className="ppc-so-swatch ppc-so-swatch--actual" /> Actual</span>
                  <span><span className="ppc-so-swatch ppc-so-swatch--current" /> In progress</span>
                  <span><span className="ppc-so-swatch" /> Forecast</span>
                </div>
                <div className="ppc-tod-segment" role="group" aria-label="Units">
                  <button type="button" className={`ppc-tod-btn ppc-tod-btn--sm${!isDollars ? ' is-active' : ''}`} onClick={() => setUnit('hours')} aria-pressed={!isDollars}>
                    Hours
                  </button>
                  <button type="button" className={`ppc-tod-btn ppc-tod-btn--sm${isDollars ? ' is-active' : ''}`} onClick={() => setUnit('dollars')} aria-pressed={isDollars}>
                    Dollars
                  </button>
                </div>
              </div>
            </div>

            {table.rows.length === 0 ? (
              <div className="ppc-tod-card__note">Nobody has hours forecast or logged in this task order&rsquo;s period yet.</div>
            ) : (
              <div className="ppc-tod-tablewrap">
                <table className="ppc-tod-table ppc-so-table">
                  <thead>
                    <tr>
                      <SortableTh sortKey="name" sort={sort} onSort={toggleSort} className="ppc-so-sticky" align="left">Person</SortableTh>
                      <SortableTh sortKey="rate" sort={sort} onSort={toggleSort}>
                        {table.rows.some((p) => p.rates.length > 1) ? 'Rates' : 'Rate'}
                      </SortableTh>
                      {tableMonths.map((ms) => (
                        <SortableTh key={ms} sortKey={ms} sort={sort} onSort={toggleSort} className={`ppc-so-month ppc-so-month--${monthKind(ms, currentMonth)}`}>
                          {formatMonthShort(ms)}
                        </SortableTh>
                      ))}
                      <SortableTh sortKey="total" sort={sort} onSort={toggleSort}>Total</SortableTh>
                      <SortableTh sortKey="share" sort={sort} onSort={toggleSort}>{isDollars ? 'Share of $' : 'Share of hours'}</SortableTh>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((p) => (
                      <tr key={p.userId}>
                        <td className="ppc-so-sticky">{p.name}</td>
                        <td className="ppc-tod-num">
                          {p.rates.length === 0 ? (
                            <span className="ppc-tod-table__muted">{'\u2014'}</span>
                          ) : (
                            <div className="ppc-so-rates">
                              {p.rates.map((r) => (
                                <div key={r.from} className="ppc-so-rate">
                                  {r.rate > 0 ? <span>{formatMoney(r.rate)}</span> : <span className="ppc-so-text--bad">No rate</span>}
                                  {p.rates.length > 1 ? <span className="ppc-so-rate__when">{formatRateRun(r)}</span> : null}
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                        {tableMonths.map((ms) => {
                          const c = p.cells[ms];
                          const value = isDollars ? c.dollars : c.hours;
                          const tip = c.kind === 'forecast'
                            ? null
                            : `Forecast ${formatHoursAlways(c.forecastHours)} h \u00b7 Actual ${formatHoursAlways(c.actualHours)} h`;
                          const content = <span className="ppc-tod-num">{cellText(value)}</span>;
                          return (
                            <td key={ms} className={`ppc-so-cell ppc-so-cell--${c.kind}`}>
                              {tip && (c.forecastHours || c.actualHours) ? (
                                <Whisper placement="top" trigger="hover" speaker={<Tooltip>{tip}</Tooltip>}>{content}</Whisper>
                              ) : content}
                            </td>
                          );
                        })}
                        <td className="ppc-tod-num ppc-so-total">{isDollars ? formatMoney(p.tDollars) : formatHoursAlways(p.tHours)}</td>
                        <td className="ppc-tod-num">
                          {(() => {
                            // Dollars count ODCs in the whole, as the Total row does; hours are labor only.
                            const part = isDollars ? p.tDollars : p.tHours;
                            const whole = isDollars ? table.dollars : table.hours;
                            return (
                              <div className="ppc-so-share">
                                <div className="ppc-so-share__track">
                                  <div className="ppc-so-share__bar" style={{ width: `${whole > 0 ? Math.min(100, (part / whole) * 100) : 0}%` }} />
                                </div>
                                <span>{whole > 0 ? formatPct(part / whole, 0) : '\u2014'}</span>
                              </div>
                            );
                          })()}
                        </td>
                      </tr>
                    ))}
                    {isDollars && hasOdc ? (
                      <tr className="ppc-so-odc">
                        <td className="ppc-so-sticky">Other direct costs</td>
                        <td />
                        {tableMonths.map((ms) => {
                          const c = model.odcCells[ms];
                          return (
                            <td key={ms} className={`ppc-so-cell ppc-so-cell--${c.kind}`}>
                              <span className="ppc-tod-num">{cellText(c.dollars)}</span>
                            </td>
                          );
                        })}
                        <td className="ppc-tod-num ppc-so-total">{formatMoney(table.odcTotal)}</td>
                        <td className="ppc-tod-num">{table.dollars > 0 ? formatPct(table.odcTotal / table.dollars, 0) : '\u2014'}</td>
                      </tr>
                    ) : null}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td className="ppc-so-sticky">{isDollars ? 'Total' : 'Total hours'}</td>
                      <td />
                      {tableMonths.map((ms) => (
                        <td key={ms} className="ppc-tod-num">
                          {isDollars ? formatMoneyCompact(table.monthTotals[ms].dollars) : formatHoursAlways(table.monthTotals[ms].hours)}
                        </td>
                      ))}
                      <td className="ppc-tod-num">{isDollars ? formatMoney(table.dollars) : formatHoursAlways(table.hours)}</td>
                      <td />
                    </tr>
                    {isDollars && budget > 0 ? (
                      <tr className="ppc-so-foot-sub">
                        <td className="ppc-so-sticky">Cumulative % of budget</td>
                        <td />
                        {tableMonths.map((ms) => {
                          const pct = table.monthTotals[ms].cumulative / budget;
                          return (
                            <td key={ms} className={`ppc-tod-num${pct > 1 ? ' ppc-so-text--bad' : ''}`}>{formatPct(pct, 0)}</td>
                          );
                        })}
                        <td className={`ppc-tod-num${table.dollars > budget ? ' ppc-so-text--bad' : ''}`}>{formatPct(table.dollars / budget)}</td>
                        <td />
                      </tr>
                    ) : null}
                  </tfoot>
                </table>
              </div>
            )}
            <div className="ppc-tod-card__note ppc-so-note">
              Months that are over count actual hours; months still to come count the forecast. {formatMonthLong(currentMonth)} is in
              progress and counts whichever is higher so far. Hours are priced at each person&rsquo;s rate for that month.
              {!isDollars && hasOdc ? ' Switch to Dollars to see other direct costs.' : ''}
              {hiddenMonthCount > 0 && tableMonths.length > 0
                ? ` Only the task order\u2019s months (${formatMonthShort(tableMonths[0])} \u2013 ${formatMonthShort(tableMonths[tableMonths.length - 1])}) are shown here; ${hiddenMonthCount} month${hiddenMonthCount === 1 ? '' : 's'} outside it still count in the projected total above.`
                : ''}
            </div>
          </section>

        </>
      )}
    </div>
  );
}
